/**
 * OrgConfigEditSlideout — Stage 2c-2 (P0-IDENT).
 *
 * Edit form for the Org-scope identity baseline configuration. Reached
 * from IdentityOrganizationPageV2 via the "Edit" button in the page
 * header. Fields cover the V200 identity_org_config row:
 *
 *   - Trust domain (DNS-form lowercase)
 *   - Default SVID TTL (seconds, with unit picker)
 *   - Rotation window fraction (0,1)
 *   - JWT-SVID TTL (seconds)
 *   - CA TTL (seconds)
 *   - Failure threshold (strikes + window seconds)
 *   - PagerDuty escalation tier (P1/P2/P3/P4/P5)
 *
 * Calls PUT /api/v1/identity/organization on submit. Client-side
 * validation mirrors the backend Bean Validation rules (positive TTLs,
 * fraction in (0,1), JWT TTL ≤ SVID TTL). Server-side validation is
 * the authoritative gate; client-side just prevents obvious mistakes
 * before they hit the backend.
 *
 * On successful save, calls onSaved() so the parent page can refresh.
 */
import React, { useEffect, useState } from 'react';
import { Save, X as XIcon, AlertTriangle } from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Card } from '../Card';
import { Pill } from '../Pill';
import {
  getIdentityOrganization,
  updateIdentityOrganization,
} from '../../../services/api';

export function OrgConfigEditSlideout({ isOpen, onClose, onSaved }) {
  // Form state — explicit so partial-update semantics are clear.
  // Empty string = "don't send this field" in the PUT body (preserves
  // existing server-side value).
  const [trustDomain, setTrustDomain] = useState('');
  const [svidTtl, setSvidTtl] = useState('');
  const [rotation, setRotation] = useState('');
  const [jwtTtl, setJwtTtl] = useState('');
  const [caTtl, setCaTtl] = useState('');
  const [strikes, setStrikes] = useState('');
  const [windowSec, setWindowSec] = useState('');
  const [pdTier, setPdTier] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load current values when slideout opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getIdentityOrganization().then((res) => {
      if (cancelled) return;
      if (res?.ok === false) {
        setError(res.error || 'Could not load current Org config.');
        setLoading(false);
        return;
      }
      // The GET response shape is the full page payload — the rotation
      // defaults are display strings, not raw numbers. We can't reliably
      // round-trip "1 hour" back to 3600, so on initial open the fields
      // start EMPTY and the user types only what they want to change.
      // Trust domain DOES round-trip cleanly (it's a raw string), so
      // we extract it from the trustDomains list's primary entry.
      const data = res?.data;
      const primaryTd = (data?.trustDomains || []).find((d) => d?.id === 'primary');
      if (primaryTd?.spiffeId) {
        // Strip the "spiffe://" prefix — the input edits the DNS form.
        const td = String(primaryTd.spiffeId).replace(/^spiffe:\/\//, '');
        setTrustDomain(td);
      }
      setLoading(false);
    }).catch((e) => {
      if (!cancelled) {
        setError(e?.message || String(e));
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Reset form state when slideout closes — prevents stale values
  // bleeding into the next open.
  useEffect(() => {
    if (isOpen) return;
    setSvidTtl(''); setRotation(''); setJwtTtl(''); setCaTtl('');
    setStrikes(''); setWindowSec(''); setPdTier('');
    setError(null);
  }, [isOpen]);

  const handleSave = async () => {
    setError(null);

    // Build the PUT body — only include fields the user actually set.
    const body = {};
    if (trustDomain.trim()) body.trustDomain = trustDomain.trim().toLowerCase();
    if (svidTtl) body.defaultSvidTtlSeconds = parseInt(svidTtl, 10);
    if (rotation) body.rotationWindowFraction = parseFloat(rotation);
    if (jwtTtl) body.jwtSvidTtlSeconds = parseInt(jwtTtl, 10);
    if (caTtl) body.caTtlSeconds = parseInt(caTtl, 10);
    if (strikes) body.failureThresholdStrikes = parseInt(strikes, 10);
    if (windowSec) body.failureThresholdWindowS = parseInt(windowSec, 10);
    if (pdTier) body.escalationPagerdutyTier = pdTier;

    // Client-side validation mirrors backend rules.
    if (body.defaultSvidTtlSeconds != null && body.defaultSvidTtlSeconds <= 0) {
      setError('Default SVID TTL must be positive.');
      return;
    }
    if (body.rotationWindowFraction != null
        && (body.rotationWindowFraction <= 0 || body.rotationWindowFraction >= 1)) {
      setError('Rotation window fraction must be strictly between 0 and 1.');
      return;
    }
    if (body.jwtSvidTtlSeconds != null && body.defaultSvidTtlSeconds != null
        && body.jwtSvidTtlSeconds > body.defaultSvidTtlSeconds) {
      setError('JWT-SVID TTL must not exceed the default SVID TTL.');
      return;
    }
    if (Object.keys(body).length === 0) {
      setError('Nothing to save — set at least one field.');
      return;
    }

    setSaving(true);
    const res = await updateIdentityOrganization(body);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Save failed.');
      return;
    }
    // Success — let the parent refresh.
    if (typeof onSaved === 'function') onSaved(res?.data);
    onClose();
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={560}
      ariaLabel="Edit Org-scope identity configuration"
      header={
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            Edit Org-scope identity configuration
          </h2>
          <Pill tone="action" size="sm">Identity</Pill>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" iconLeft={<XIcon size={13} />} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" iconLeft={<Save size={13} />} onClick={handleSave}
                  disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      }
    >
      <div className="px-4 py-4 flex flex-col gap-4">
        {loading && (
          <div className="text-[12px] text-v2-ink-muted">Loading current values…</div>
        )}

        {error && (
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md px-3 py-2 text-[12.5px] text-v2-danger flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="text-[11px] text-v2-ink-muted">
          Partial update — leave any field blank to preserve the current server value.
          Cleared fields are NOT sent to the backend.
        </div>

        <Card padding="md">
          <Card.Header title="Trust domain" subtitle="SPIFFE identity boundary for this org" />
          <Card.Body>
            <label className="block text-[11px] text-v2-ink-secondary mb-1">Trust domain (DNS form)</label>
            <input
              type="text"
              value={trustDomain}
              onChange={(e) => setTrustDomain(e.target.value)}
              placeholder="acmebank.spiffe.io"
              className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]"
            />
          </Card.Body>
        </Card>

        <Card padding="md">
          <Card.Header title="Rotation defaults" subtitle="SVID lifecycle baseline for every agent in the org" />
          <Card.Body>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">
                  Default SVID TTL (seconds)
                </label>
                <input type="number" min="1" value={svidTtl}
                       onChange={(e) => setSvidTtl(e.target.value)}
                       placeholder="3600" className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]" />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">
                  Rotation window fraction (0&lt;x&lt;1)
                </label>
                <input type="number" step="0.001" min="0.001" max="0.999"
                       value={rotation} onChange={(e) => setRotation(e.target.value)}
                       placeholder="0.667" className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]" />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">
                  JWT-SVID TTL (seconds)
                </label>
                <input type="number" min="1" value={jwtTtl}
                       onChange={(e) => setJwtTtl(e.target.value)}
                       placeholder="300" className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]" />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">
                  CA TTL (seconds)
                </label>
                <input type="number" min="1" value={caTtl}
                       onChange={(e) => setCaTtl(e.target.value)}
                       placeholder="86400" className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]" />
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card padding="md">
          <Card.Header title="Failure escalation" subtitle="When rotation strikes accumulate" />
          <Card.Body>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">Strikes</label>
                <input type="number" min="1" value={strikes}
                       onChange={(e) => setStrikes(e.target.value)}
                       placeholder="3" className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]" />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">Window (seconds)</label>
                <input type="number" min="1" value={windowSec}
                       onChange={(e) => setWindowSec(e.target.value)}
                       placeholder="900" className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]" />
              </div>
              <div>
                <label className="block text-[11px] text-v2-ink-secondary mb-1">PagerDuty tier</label>
                <select value={pdTier} onChange={(e) => setPdTier(e.target.value)} className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]">
                  <option value="">— unchanged —</option>
                  <option value="P1">P1</option>
                  <option value="P2">P2</option>
                  <option value="P3">P3</option>
                  <option value="P4">P4</option>
                  <option value="P5">P5</option>
                </select>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </Slideout>
  );
}

export default OrgConfigEditSlideout;
